import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, TrendingUp, TrendingDown, Calendar, DollarSign, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => currentYear - i);

const fmt = (v) => {
  if (v == null) return '$0.00';
  const n = Number(v);
  const sign = n >= 0 ? '' : '-';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const gainColor = (v) => (v >= 0 ? 'text-green-400' : 'text-red-400');

export default function TaxPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(currentYear);
  const [mode, setMode] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async (y) => {
    setLoading(true);
    try {
      const params = { year: y };
      if (mode) params.mode = mode;
      const res = await axios.get(`${API_V2}/tax/report`, { params, withCredentials: true });
      setReport(res.data);
      if (!mode) setMode(res.data.mode);
    } catch (err) {
      toast.error('Failed to load tax report');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { fetchReport(year); }, [year, fetchReport]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ year, mode: mode || 'simulation' });
      window.open(`${API_V2}/tax/export?${params}`, '_blank');
      toast.success('CSV download started');
    } catch {
      toast.error('Export failed');
    }
  };

  const s = report?.summary || {};

  return (
    <div className="min-h-screen bg-black text-[#00FF41] p-4 font-mono" data-testid="tax-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/dashboard')}
            className="matrix-btn rounded-none text-xs h-8 border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black"
            data-testid="tax-back-btn"
          >
            <ArrowLeft className="h-3 w-3 mr-1" />
            DASHBOARD
          </Button>
          <h1 className="text-lg font-bold tracking-wider">
            <FileText className="h-4 w-4 inline mr-2" />
            TAX REPORT
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Year selector */}
          <div className="flex border border-[#003B00]" data-testid="year-selector">
            {YEARS.map(y => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-3 py-1 text-xs transition-colors ${
                  year === y
                    ? 'bg-[#00FF41] text-black font-bold'
                    : 'text-[#00FF41] hover:bg-[#001a00]'
                }`}
                data-testid={`year-btn-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
          {/* Mode toggle */}
          {mode && (
            <div className="flex border border-[#003B00]" data-testid="mode-selector">
              {['simulation', 'live'].map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1 text-xs uppercase transition-colors ${
                    mode === m
                      ? 'bg-[#00FF41] text-black font-bold'
                      : 'text-[#00FF41] hover:bg-[#001a00]'
                  }`}
                  data-testid={`mode-btn-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
          <Button
            onClick={handleExport}
            className="matrix-btn rounded-none text-xs h-8 border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-black"
            data-testid="tax-export-btn"
          >
            <Download className="h-3 w-3 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-[#00FF41]/60">Loading tax data...</div>}

      {!loading && report && (
        <>
          {/* Capital Gains Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tax-summary">
            <Card className="bg-[#0a0a0a] border-[#003B00]">
              <CardContent className="p-3">
                <div className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> TOTAL PROCEEDS
                </div>
                <div className="text-sm font-bold text-[#00FF41]">{fmt(s.total_proceeds)}</div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a0a0a] border-[#003B00]">
              <CardContent className="p-3">
                <div className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> COST BASIS
                </div>
                <div className="text-sm font-bold text-[#00FF41]">{fmt(s.total_cost_basis)}</div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a0a0a] border-[#003B00]">
              <CardContent className="p-3">
                <div className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> SHORT-TERM
                </div>
                <div className={`text-sm font-bold ${gainColor(s.short_term_gain_loss)}`}>
                  {fmt(s.short_term_gain_loss)}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0a0a0a] border-[#003B00]">
              <CardContent className="p-3">
                <div className="text-[10px] text-gray-500 mb-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> LONG-TERM
                </div>
                <div className={`text-sm font-bold ${gainColor(s.long_term_gain_loss)}`}>
                  {fmt(s.long_term_gain_loss)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Net Gain/Loss banner */}
          <Card className="bg-[#0a0a0a] border-[#003B00] mb-6" data-testid="net-gain-card">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500">NET CAPITAL GAIN / LOSS ({year})</div>
                <div className={`text-2xl font-bold ${gainColor(s.total_gain_loss)}`}>
                  {fmt(s.total_gain_loss)}
                </div>
              </div>
              <div className="text-right text-xs text-gray-500">
                <div>{s.total_dispositions || 0} dispositions</div>
                <div>Fees: {fmt(s.total_fees)}</div>
                <div className="uppercase mt-1 text-[#00FF41]/60">{mode} mode</div>
              </div>
            </CardContent>
          </Card>

          {/* Per-Coin Breakdown */}
          {report.coin_breakdown?.length > 0 && (
            <Card className="bg-[#0a0a0a] border-[#003B00] mb-6" data-testid="coin-breakdown">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm text-[#00FF41]">PER-COIN BREAKDOWN</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-[#003B00]">
                        <th className="text-left py-1 pr-4">COIN</th>
                        <th className="text-right py-1 pr-4"># SELLS</th>
                        <th className="text-right py-1 pr-4">PROCEEDS</th>
                        <th className="text-right py-1 pr-4">COST BASIS</th>
                        <th className="text-right py-1 pr-4">SHORT-TERM</th>
                        <th className="text-right py-1 pr-4">LONG-TERM</th>
                        <th className="text-right py-1">NET GAIN/LOSS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.coin_breakdown.map(c => (
                        <tr key={c.coin} className="border-b border-[#003B00]/30 hover:bg-[#001a00]">
                          <td className="py-1.5 pr-4 text-[#00FF41] font-bold">{c.coin}</td>
                          <td className="py-1.5 pr-4 text-right text-gray-400">{c.count}</td>
                          <td className="py-1.5 pr-4 text-right">{fmt(c.proceeds)}</td>
                          <td className="py-1.5 pr-4 text-right text-gray-400">{fmt(c.cost_basis)}</td>
                          <td className={`py-1.5 pr-4 text-right ${gainColor(c.short_term)}`}>{fmt(c.short_term)}</td>
                          <td className={`py-1.5 pr-4 text-right ${gainColor(c.long_term)}`}>{fmt(c.long_term)}</td>
                          <td className={`py-1.5 text-right font-bold ${gainColor(c.gain_loss)}`}>{fmt(c.gain_loss)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Individual Dispositions (Form 8949) */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="dispositions-table">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm text-[#00FF41]">
                <Calendar className="h-3 w-3 inline mr-1" />
                INDIVIDUAL DISPOSITIONS (Form 8949)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {report.dispositions?.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">
                  No dispositions found for {year} in {mode} mode
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-[#0a0a0a]">
                      <tr className="text-gray-500 border-b border-[#003B00]">
                        <th className="text-left py-1 pr-3">ASSET</th>
                        <th className="text-left py-1 pr-3">ACQUIRED</th>
                        <th className="text-left py-1 pr-3">SOLD</th>
                        <th className="text-right py-1 pr-3">PROCEEDS</th>
                        <th className="text-right py-1 pr-3">COST BASIS</th>
                        <th className="text-right py-1 pr-3">GAIN/LOSS</th>
                        <th className="text-left py-1 pr-3">TERM</th>
                        <th className="text-right py-1">DAYS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.dispositions.map((d, i) => (
                        <tr key={i} className="border-b border-[#003B00]/20 hover:bg-[#001a00]">
                          <td className="py-1 pr-3 text-[#00FF41]">{d.amount} {d.coin}</td>
                          <td className="py-1 pr-3 text-gray-400">{d.date_acquired}</td>
                          <td className="py-1 pr-3 text-gray-400">{d.date_sold}</td>
                          <td className="py-1 pr-3 text-right">{fmt(d.proceeds)}</td>
                          <td className="py-1 pr-3 text-right text-gray-400">{fmt(d.cost_basis)}</td>
                          <td className={`py-1 pr-3 text-right font-bold ${gainColor(d.gain_loss)}`}>{fmt(d.gain_loss)}</td>
                          <td className="py-1 pr-3">
                            <span className={`text-[9px] px-1 ${d.term === 'Long-Term' ? 'text-cyan-400 border border-cyan-800' : 'text-yellow-400 border border-yellow-800'}`}>
                              {d.term === 'Long-Term' ? 'LT' : 'ST'}
                            </span>
                          </td>
                          <td className="py-1 text-right text-gray-500">{d.holding_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* IRS Disclaimer */}
          <div className="mt-4 text-[9px] text-gray-600 text-center">
            This report is for informational purposes only and does not constitute tax advice.
            Consult a qualified tax professional. Uses FIFO (First-In, First-Out) cost basis method.
          </div>
        </>
      )}
    </div>
  );
}
